import gameConfigurations from '@configs/gameConfigurations'
import { create } from 'zustand'
import {
    BaseGameType,
    ViewportType,
    DefaultLocalization,
    SlotGameConfigurationState,
    BaseGamesDefaultLocalization,
    GameUIMessageType,
    BackgroundMusicClickedMessage,
} from 'game-ui-types'
import { ComponentConfiguration } from '@configs/types'
import { getParam, getTournamentMode, hasFlag, isStandalone } from '@amusnet/react-ui'

export interface ConfigurationStoreState extends SlotGameConfigurationState {
    hasWidgetInUI: boolean // TODO: remove when promo removed from platform
    shouldHideFullScreenButton: boolean
    isExpoTournamentModeHidden: boolean
    shouldUseJackpotComponent: boolean
    setConfiguration: (config: Partial<SlotGameConfigurationState>) => void
    setLanguage: (language: string) => void
    getComponentConfig: (component: string) => ComponentConfiguration
    computedReelSizes: () => Record<ViewportType, { width: number; height: number; x: number; y: number }>
    setIsSoundMuted: (isMuted: boolean, isByUser?: boolean) => void
    setIsBackgroundMusicMuted: (isMuted: boolean) => void
    setIsFullscreen: (isFullscreen: boolean) => void
    isGameLoaded: boolean
    setIsGameLoaded: (isGameLoaded: boolean) => void
    shouldUseExternalMenu: boolean
    paytableData: Record<string, unknown>
    setPaytableData: (paytableData: Record<string, unknown>) => void
    setPaytableUrl: (paytableUrl: string) => void
    paytableBasePath: string
    paytableLanguages: string[]
    setPaytableBasePath: (paytableBasePath: string) => void
    isPaytableConfirmationAccepted: boolean
    setIsPaytableConfirmationAccepted: (isPaytableConfirmationAccepted: boolean) => void
    playerUid: string
    setPlayerUid: (playerUid: string) => void
    apiBaseUrl: string
    setApiBaseUrl: (apiBaseUrl: string) => void
    portalUid: string
    setPortalUid: (portalUid: string) => void
}

/**
 * Builds a language-specific paytable URL from `paytableBasePath` when the
 * engine sends `paytableLanguages`. Falls back to the platform-provided
 * `fallbackUrl` when the engine is older (no `paytableLanguages`) or the
 * current language is not supported by the engine.
 */
export const resolvePaytableUrl = (
    language: string,
    paytableLanguages: string[],
    paytableBasePath: string,
    fallbackUrl: string
): string => {
    if (isStandalone) {
        const { assetsPath, modeType } = window.gameConfig!
        const promoSuffix = modeType ? `/${modeType}` : ''
        const paytableLanguage = paytableLanguages.includes(language) ? language : 'en'

        return `../paytable/${assetsPath}${promoSuffix}/${paytableLanguage}.html?v=${Date.now()}`
    }

    if (paytableLanguages.length > 0 && paytableBasePath && paytableLanguages.includes(language)) {
        return `${paytableBasePath}/${language}.html?v=${Date.now()}`
    }

    return fallbackUrl || ''
}

export const useConfigurationStore = create<ConfigurationStoreState>((set, get) => ({
    paytableData: {},
    setPaytableData: (paytableData) => set({ paytableData }),
    paytableUrl: '',
    setPaytableUrl: (paytableUrl) => set({ paytableUrl }),
    paytableBasePath: '',
    paytableLanguages: [],
    setPaytableBasePath: (paytableBasePath) => set({ paytableBasePath }),
    gameHistoryApi: '',
    gameHistoryUrl: '',
    gin: -1,
    token: '',
    betOptionActiveIndex: -1,
    gameType: BaseGameType.SLOT_CONFIG_A, // Default game type
    currency: 'EUR', // Default currency
    currencyHideCents: false,
    language: 'en',
    playerName: '',
    playerUid: '',
    amount: 0,
    betGroupId: '',
    shouldHideTotalFreespinsText: false,
    betOptions: [], // Default bet options
    hasCustomResult: false, // Default custom result
    hasBuyBonus: false, // Default buy bonus state
    hasDoubleChance: false, // Default double chance state
    shouldHideHelpSection: false, // Default help section visibility
    hasLuckySpins: false, // Default lucky spins state
    doubleChanceMultiplier: 1, // Default double chance multiplier
    initialFeatureName: 'NONE', // Default initial feature name
    autoplayOptions: [], // Default autoplay options
    autoplaySettings: [],
    isAutoplayDisabled: false, // Default autoplay disabled
    shouldHideDisabledAutoplayButton: false, // Default hide disabled autoplay button
    centsVisibilityMoneyLimit: 9999, // Default cents visibility money limit
    shouldShowCentsOnMoneyLimit: false, // Default show cents on money limit
    shouldBalanceUpdateImmediately: false, // Default balance update immediately state
    shouldBalanceSubtractOnly: Boolean(getParam('extraFeatures')?.includes('shouldBalanceSubtractOnly:true')), // When active, balance ignores wins/collects and only decreases on bets
    chanceOfMaxWinProbability: '1:25000000', // Default chanceOfMaxWinProbability
    shouldShowSpeedButton: false, // Default speed button visibility
    shouldEnableUltraSpeed: false, // Default ultra speed state
    shouldShowLeftHandModeButton: false, // Default left hand mode button visibility
    shouldUseExternalMenu: false, // Default external menu button state
    shouldShowPaytableConfirmation: false, // Default should show paytable confirmation state
    shouldDisableTurbospin: false, // Default turbospin state
    shouldDisableSpaceKey: false, // Default space key state
    shouldShowUTCTime: false, // Default UTC time visibility
    sendTotalsInfo: false, // Default send totals info
    minimumSpinTimeDuration: 0, // Default minimum spin time duration - value in seconds
    viewport: ViewportType.MOBILE_PORTRAIT, // Default viewport
    localization: { ...DefaultLocalization, ...BaseGamesDefaultLocalization },
    isSoundMuted: false,
    isBackgroundMusicMuted: false,
    isFullscreen: false,
    isGameLoaded: false, // Default game loaded state
    isHistoryMode: false, // Default history mode
    isRemoteGame: false, // Default remote game
    isPaytableConfirmationAccepted: false, // Default paytable confirmation switch state
    shouldUseDynamicStopButtonSize: false,
    shouldOpenAutoplaySettings: hasFlag('forceMenuAutoplayTab'), // Open autoplay settings on autoplay button click by default
    numReels: 5, // Default value
    buyBonusConfig: {
        maxBet: 800,
        choiceCount: 1,
        layout: {
            headerColors: [],
            bodyColors: [],
            footerColors: [],
            allSymbolsBackgroundColors: [],
        },
        features: [
            {
                name: 'BUY_BONUS',
                label: 'Bonus',
                multiplier: 20,
                labelFontSize: '36px',
            },
        ],
        hasGuaranteedWin: false, // Default guaranteed win state
        shouldTheFeatureStayActive: false, // Default feature stay active state
    },
    hasExternalBetAmountPlayerInfo: false, // Default support external bet amount in player info
    hasLastWinPlayerInfoPortrait: true, // Default show last win info in portrait
    shouldUseAmuseBoost: true,
    hasWidgetInUI:
        (isStandalone || getParam('extraFeatures')?.includes('widget:ui')) && getTournamentMode() !== 'expo:hidden',
    isExpoTournamentModeHidden: getTournamentMode() === 'expo:hidden',
    shouldHideFullScreenButton: getParam('extraFeatures')?.includes('shouldHideFullScreenButton:true'),
    shouldUseJackpotComponent:
        (isStandalone && !navigator.webdriver) || getParam('extraFeatures')?.includes('shouldUseJackpotComponent:true'),
    canvasDimensions: {
        // Default canvas sizes
        [ViewportType.MOBILE_PORTRAIT]: { width: 810, height: 1440 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 1440, height: 810 },
        [ViewportType.DESKTOP]: { width: 1920, height: 1080 },
    },
    stageDimensions: {
        [ViewportType.MOBILE_PORTRAIT]: { width: 810, height: 1440 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 1440, height: 810 },
        [ViewportType.DESKTOP]: { width: 1920, height: 1080 },
    },
    reel: {
        // Use computedReelSizes when using the values
        // Width of a single reel
        // Default reel values for slot config A
        [ViewportType.MOBILE_PORTRAIT]: { width: 144, height: 432, x: 0, y: 0, reelSpacing: 0 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 198, height: 594, x: 0, y: 0, reelSpacing: 0 },
        [ViewportType.DESKTOP]: { width: 264, height: 792, x: 0, y: 0, reelSpacing: 0 },
    },

    skipWinAnimationsCoef: -1,

    // Update store configuration
    setConfiguration: (config) => {
        set((state) => ({
            ...state,
            ...config,
            paytableUrl: state.paytableUrl || config.paytableUrl,
            shouldUseExternalMenu: true,
        }))
    },

    setLanguage: (language: string) => set({ language }),

    // Retrieve component-specific configuration with fallback
    getComponentConfig: (component) => {
        const { viewport, gameType } = get()

        // Current game type configuration
        const gameConfig = gameConfigurations[gameType]?.viewports?.[viewport]?.[component]

        // Fallback to base configuration if current is missing
        const baseConfig = gameConfigurations[BaseGameType.SLOT_CONFIG_A].viewports?.[viewport]?.[component]

        return {
            ...baseConfig, // Base properties
            ...gameConfig, // Override with current type properties
        }
    },

    computedReelSizes: () => {
        // Checks the dimensions of the stage and the canvas and if they're not equal scales the value accordingly
        const { reel, stageDimensions, canvasDimensions } = get()

        return Object.values(ViewportType).reduce(
            (acc, v) => {
                acc[v] = { ...reel[v] }

                if (stageDimensions[v].width !== canvasDimensions[v].width) {
                    acc[v].width = (reel[v].width * stageDimensions[v].width) / canvasDimensions[v].width
                    acc[v].x = (reel[v].x * stageDimensions[v].width) / canvasDimensions[v].width
                }

                if (stageDimensions[v].height !== canvasDimensions[v].height) {
                    acc[v].height = (reel[v].height * stageDimensions[v].height) / canvasDimensions[v].height
                    acc[v].y = (reel[v].y * stageDimensions[v].height) / canvasDimensions[v].height
                }

                return acc
            },
            {} as Record<ViewportType, { width: number; height: number; x: number; y: number }>
        )
    },
    setIsSoundMuted: (isMuted: boolean, isByUser = true) => {
        set({ isSoundMuted: isMuted })

        if (!isByUser) {
            return
        }

        // we moved the postMessage logic into zustand because we need to fire it from 2 places
        const soundClickedMessage = { type: GameUIMessageType.SOUND_CLICKED, payload: { isMuted } }

        window.postMessage(soundClickedMessage, '*')
    },
    setIsBackgroundMusicMuted: (isMuted) => {
        set({ isBackgroundMusicMuted: isMuted })

        const { shouldUseExternalMenu } = get()

        // TODO: Delete the if check when new menu is tested
        if (shouldUseExternalMenu) {
            const backgroundMusicMutedMessage: BackgroundMusicClickedMessage = {
                type: GameUIMessageType.BACKGROUND_MUSIC_CLICKED,
            }

            window.postMessage(backgroundMusicMutedMessage, '*')
        }
    },
    setIsFullscreen: (isFullscreen) => set({ isFullscreen }),
    setIsGameLoaded: (isGameLoaded) => set({ isGameLoaded }),
    setIsPaytableConfirmationAccepted: (isPaytableConfirmationAccepted) => set({ isPaytableConfirmationAccepted }),
    setPlayerUid: (playerUid: string) => set({ playerUid }),
    apiBaseUrl: '',
    setApiBaseUrl: (apiBaseUrl: string) => set({ apiBaseUrl }),
    portalUid: '',
    setPortalUid: (portalUid: string) => set({ portalUid }),
}))
